/**
 * Token Manager
 * 
 * Manages token storage, retrieval, and refresh for dual authentication types:
 * - Cognito tokens (desktop devices) - uses existing storage keys
 * - Federate tokens (mobile devices) - uses federate_ prefixed keys
 * 
 * Maintains backward compatibility with existing Cognito token storage.
 */

import {
  TokenManager as ITokenManager,
  TokenSet,
  AuthenticationType,
  FEDERATE_TOKEN_KEYS,
  COGNITO_TOKEN_KEYS
} from '../types/deviceFlowTypes';
import { deviceDetectionService } from './deviceDetectionService';

export class TokenManager implements ITokenManager {
  
  /**
   * Store tokens in localStorage with appropriate prefixes based on auth type
   * Requirements 1.4, 2.2, 3.1, 3.2, 3.3, 3.4
   */
  storeTokens(tokens: TokenSet, type: AuthenticationType): void {
    try {
      console.log(`Storing ${type} tokens`);

      if (type === 'federate') {
        // Store federate tokens with federate_ prefixes (Requirements 3.1, 3.2, 3.3, 3.4)
        localStorage.setItem(FEDERATE_TOKEN_KEYS.ID_TOKEN, tokens.idToken);
        localStorage.setItem(FEDERATE_TOKEN_KEYS.ACCESS_TOKEN, tokens.accessToken);
        
        if (tokens.refreshToken) {
          localStorage.setItem(FEDERATE_TOKEN_KEYS.REFRESH_TOKEN, tokens.refreshToken);
        }
      } else if (type === 'cognito') {
        // Store Cognito tokens using existing keys (Requirement 2.2)
        localStorage.setItem(COGNITO_TOKEN_KEYS.ACCESS_TOKEN, tokens.accessToken);
        localStorage.setItem(COGNITO_TOKEN_KEYS.ID_TOKEN, tokens.idToken);
        
        if (tokens.refreshToken) {
          localStorage.setItem(COGNITO_TOKEN_KEYS.REFRESH_TOKEN, tokens.refreshToken);
        }
      }

      // Store auth type for future reference
      localStorage.setItem('auth_type', type);
      
      console.log(`${type} tokens stored successfully`);
    } catch (error) {
      console.error(`Failed to store ${type} tokens:`, error);
      throw new Error(`Token storage failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Retrieve tokens from localStorage based on auth type
   */
  getTokens(type: AuthenticationType): TokenSet | null {
    try {
      let accessToken: string | null = null;
      let idToken: string | null = null;
      let refreshToken: string | null = null;

      if (type === 'federate') {
        accessToken = localStorage.getItem(FEDERATE_TOKEN_KEYS.ACCESS_TOKEN);
        idToken = localStorage.getItem(FEDERATE_TOKEN_KEYS.ID_TOKEN);
        refreshToken = localStorage.getItem(FEDERATE_TOKEN_KEYS.REFRESH_TOKEN);
      } else if (type === 'cognito') {
        accessToken = localStorage.getItem(COGNITO_TOKEN_KEYS.ACCESS_TOKEN);
        idToken = localStorage.getItem(COGNITO_TOKEN_KEYS.ID_TOKEN);
        refreshToken = localStorage.getItem(COGNITO_TOKEN_KEYS.REFRESH_TOKEN);
        
        // Handle legacy Cognito token storage (existing authService uses 'token' key)
        if (!accessToken || !idToken) {
          const legacyToken = localStorage.getItem('token');
          if (legacyToken) {
            // For legacy Cognito tokens, both access and ID token are the same
            accessToken = legacyToken;
            idToken = legacyToken;
            refreshToken = refreshToken || ''; // No refresh token in legacy format
          }
        }
      }

      // Return null if essential tokens are missing
      if (!accessToken || !idToken) {
        return null;
      }

      // Extract expiry from access token
      const expiresAt = this.extractTokenExpiry(accessToken);

      return {
        accessToken,
        idToken,
        refreshToken: refreshToken || '',
        expiresAt
      };

    } catch (error) {
      console.error(`Failed to retrieve ${type} tokens:`, error);
      return null;
    }
  }

  /**
   * Refresh access token using refresh token
   * Requirements 5.1, 5.2
   */
  async refreshAccessToken(type: AuthenticationType): Promise<TokenSet> {
    try {
      console.log(`Refreshing ${type} access token`);

      const currentTokens = this.getTokens(type);
      if (!currentTokens || !currentTokens.refreshToken) {
        throw new Error(`No refresh token available for ${type} authentication`);
      }

      if (type === 'federate') {
        // For federate tokens, use the device flow handler to refresh
        const { deviceFlowHandler } = await import('./deviceFlowHandler');
        const refreshedTokens = await deviceFlowHandler.refreshTokens();
        
        // Convert JWTTokens to TokenSet format
        const tokenSet: TokenSet = {
          accessToken: refreshedTokens.accessToken,
          idToken: refreshedTokens.idToken,
          refreshToken: refreshedTokens.refreshToken,
          expiresAt: refreshedTokens.expiresAt
        };

        // Store the refreshed tokens
        this.storeTokens(tokenSet, 'federate');
        
        console.log('Federate tokens refreshed successfully');
        return tokenSet;
        
      } else if (type === 'cognito') {
        // For Cognito, AWS Amplify handles token refresh automatically
        // We just need to validate the current token and return it
        // If it's expired, the authService.login() will handle the refresh
        
        if (this.isTokenExpired(currentTokens.accessToken)) {
          throw new Error('Cognito token expired, re-authentication required');
        }

        return currentTokens;
      }

      throw new Error(`Unsupported authentication type: ${type}`);

    } catch (error) {
      console.error(`Failed to refresh ${type} tokens:`, error);
      throw new Error(`Token refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Clear tokens from localStorage
   */
  clearTokens(type: AuthenticationType): void {
    try {
      console.log(`Clearing ${type} tokens`);

      if (type === 'federate') {
        localStorage.removeItem(FEDERATE_TOKEN_KEYS.ID_TOKEN);
        localStorage.removeItem(FEDERATE_TOKEN_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(FEDERATE_TOKEN_KEYS.REFRESH_TOKEN);
      } else if (type === 'cognito') {
        localStorage.removeItem(COGNITO_TOKEN_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(COGNITO_TOKEN_KEYS.ID_TOKEN);
        localStorage.removeItem(COGNITO_TOKEN_KEYS.REFRESH_TOKEN);
        
        // Also clear the legacy 'token' key used by existing authService
        localStorage.removeItem('token');
      }

      // Clear auth type if no tokens remain
      const otherType = type === 'federate' ? 'cognito' : 'federate';
      const otherTokens = this.getTokens(otherType);
      if (!otherTokens) {
        localStorage.removeItem('auth_type');
      }

      console.log(`${type} tokens cleared successfully`);
    } catch (error) {
      console.error(`Failed to clear ${type} tokens:`, error);
      // Don't throw on cleanup operations
    }
  }

  /**
   * Check if a JWT token is expired
   */
  isTokenExpired(token: string): boolean {
    if (!token) {
      return true;
    }

    try {
      // Decode JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Token is expired if current time is past expiration
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Failed to decode token for expiration check:', error);
      return true; // Assume expired if we can't decode
    }
  }

  /**
   * Check if token should be refreshed (within 5 minutes of expiry)
   */
  shouldRefreshToken(token: string): boolean {
    if (!token) {
      return false;
    }

    try {
      // Decode JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      // Refresh if token expires within 1 minute (60 seconds)
      return payload.exp < (currentTime + 60);
    } catch (error) {
      console.error('Failed to decode token for refresh check:', error);
      return false;
    }
  }

  /**
   * Get the stored authentication type
   */
  getStoredAuthType(): AuthenticationType | null {
    try {
      const storedType = localStorage.getItem('auth_type') as AuthenticationType | null;
      
      // If no explicit auth type is stored, try to infer from available tokens
      if (!storedType) {
        const federateTokens = this.getTokens('federate');
        const cognitoTokens = this.getTokens('cognito');
        
        if (federateTokens) {
          return 'federate';
        } else if (cognitoTokens) {
          return 'cognito';
        }
        
        // Also check for legacy 'token' key used by existing authService
        const legacyToken = localStorage.getItem('token');
        if (legacyToken) {
          return 'cognito';
        }
        
        // If no tokens are found, determine based on device type
        // This helps with initial authentication flow
        const deviceType = deviceDetectionService?.getDeviceType();
        if (deviceType === 'mobile') {
          return 'federate';
        } else if (deviceType === 'desktop') {
          return 'cognito';
        }
      }
      
      return storedType;
    } catch (error) {
      console.error('Failed to get stored auth type:', error);
      return null;
    }
  }

  /**
   * Get token information for debugging
   */
  getTokenInfo(type: AuthenticationType): {
    hasTokens: boolean;
    isExpired: boolean;
    shouldRefresh: boolean;
    expiresAt: number | null;
  } {
    const tokens = this.getTokens(type);
    
    if (!tokens) {
      return {
        hasTokens: false,
        isExpired: true,
        shouldRefresh: false,
        expiresAt: null
      };
    }

    return {
      hasTokens: true,
      isExpired: this.isTokenExpired(tokens.accessToken),
      shouldRefresh: this.shouldRefreshToken(tokens.accessToken),
      expiresAt: tokens.expiresAt
    };
  }

  /**
   * Ensure tokens are valid and refreshed if needed before API calls
   * Requirements 5.1, 5.2 - Automatic token refresh
   */
  async ensureValidTokens(type: AuthenticationType): Promise<TokenSet> {
    try {
      const currentTokens = this.getTokens(type);
      
      if (!currentTokens) {
        throw new Error(`No ${type} tokens available`);
      }

      // Check if token should be refreshed (within 1 minute of expiry)
      if (this.shouldRefreshToken(currentTokens.accessToken)) {
        console.log(`${type} token needs refresh, refreshing automatically`);
        return await this.refreshAccessToken(type);
      }

      // Check if token is already expired
      if (this.isTokenExpired(currentTokens.accessToken)) {
        console.log(`${type} token is expired, attempting refresh`);
        return await this.refreshAccessToken(type);
      }

      // Token is still valid
      return currentTokens;

    } catch (error) {
      console.error(`Failed to ensure valid ${type} tokens:`, error);
      throw new Error(`Token validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Extract token expiry timestamp from JWT token
   */
  private extractTokenExpiry(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : Date.now() + (15 * 60 * 1000); // Default 15 minutes
    } catch (error) {
      console.error('Failed to extract token expiry:', error);
      return Date.now() + (15 * 60 * 1000); // Default 15 minutes
    }
  }

  /**
   * Validate token structure (basic JWT validation)
   */
  private isValidTokenStructure(token: string): boolean {
    if (!token) return false;

    try {
      // JWT tokens should have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Try to decode the header and payload
      JSON.parse(atob(parts[0])); // Header
      JSON.parse(atob(parts[1])); // Payload
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clean up invalid or corrupted tokens
   */
  cleanupInvalidTokens(): void {
    try {
      console.log('Cleaning up invalid tokens');

      // Check federate tokens
      const federateTokens = this.getTokens('federate');
      if (federateTokens) {
        const isValidAccess = this.isValidTokenStructure(federateTokens.accessToken);
        const isValidId = this.isValidTokenStructure(federateTokens.idToken);
        
        if (!isValidAccess || !isValidId) {
          console.log('Found invalid federate tokens, clearing them');
          this.clearTokens('federate');
        }
      }

      // Check cognito tokens
      const cognitoTokens = this.getTokens('cognito');
      if (cognitoTokens) {
        const isValidAccess = this.isValidTokenStructure(cognitoTokens.accessToken);
        const isValidId = this.isValidTokenStructure(cognitoTokens.idToken);
        
        if (!isValidAccess || !isValidId) {
          console.log('Found invalid cognito tokens, clearing them');
          this.clearTokens('cognito');
        }
      }

      console.log('Token cleanup completed');
    } catch (error) {
      console.error('Failed to cleanup invalid tokens:', error);
      // Don't throw on cleanup operations
    }
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
export default tokenManager;